// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import { constants, DivWrapper } from "@vanmeter/XDL/constants";
import { color, layoutContainer } from "@vanmeter/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

// <XNG-Changes>
const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };
// </XNG-Changes>

const enum fields {
    rowCount = "rowCount",
    gap = "gap",
    isFullWidth = "isFullWidth",
    customCSS = "customCSS",
    // XNG-Changes
    margin = "margin",
    overrideStyle = "overrideStyle",
    itemsList = "itemsList",
}

interface Props extends WidgetProps {
    fields: {
        [fields.rowCount]: number;
        [fields.gap]: number;
        [fields.isFullWidth]: boolean;
        [fields.customCSS]: string;
        // XNG-Changes: add custom fields
        [fields.margin]: string;
        [fields.overrideStyle]: boolean;
        [fields.itemsList]: {
            fields: {
                name: string;
                fullWidth: boolean;
                removeFooterOffset: boolean;
                addSeparate: boolean;
                backgroundColor: string;
            };
        }[];
    };
}

// <XNG-Changes>

const styles: { container?: InjectableCss; itemContainer?: InjectableCss; gridItem?: InjectableCss } = {
    container: {
        css: css`
            max-width: 100%;
            ${({ margin }: any) => margin && `margin: ${margin};`}
            > div {
                margin-bottom: ${({ gap }: any) => gap}px;
                &:last-child:not([data-remove-footer-offset="true"]) {
                    margin-bottom: 0;
                }
                &:has(> div:empty) {
                    display: none;
                }
            }
        `,
    },
    itemContainer: {
        css: css`
            width: 100%;
            ${layoutContainer}

            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background-color: ${color(backgroundColor)};
                `};

            ${({ removeFooterOffset, addSeparate }: any) => css`
                ${removeFooterOffset &&
                css`
                    &&& {
                        margin-bottom: -${constants.footer.offsetTop}px;
                    }
                `}
                ${addSeparate &&
                css`
                    & > ${DivWrapper} > div:nth-of-type(1):not(:empty) {
                        border-top: 1px solid ${color("common.border")};
                        padding-top: 24px;
                        margin-top: 24px;
                    }
                    & > ${DivWrapper} > div:empty {
                        display: none;
                    }
                `}
            `}
        `,
    },
    gridItem: {
        css: css`
            &&& {
                @media print {
                    display: block;
                }
            }
        `,
    },
};
// </XNG-Changes>

// A widget that makes a comfigurable number of row zones within its container.
const Rows = ({ fields: { rowCount, gap, isFullWidth, customCSS, margin, overrideStyle, itemsList }, id }: Props) => {
    const rows: JSX.Element[] = [];
    // <XNG-Changes>
    if (overrideStyle) {
        itemsList?.forEach((item, index) => {
            const {
                fullWidth: isFullWidthItem,
                removeFooterOffset,
                addSeparate,
                name: itemName = "",
                backgroundColor,
            } = item.fields;
            const name = itemName.replace(/\s/g, "");
            // "full-width" class for "LayoutContainer" style
            const className = `${isFullWidthItem ? "full-width row" : "row"}`;

            const itemProps = {
                addSeparate,
                removeFooterOffset: removeFooterOffset && index === itemsList.length - 1,
                backgroundColor,
            };

            rows.push(
                <StyledWrapper
                    key={`StyledWrapper-${id}-${name || index}`}
                    className={className}
                    data-remove-footer-offset={
                        removeFooterOffset && index === itemsList.length - 1 ? "true" : undefined
                    }
                    {...itemProps}
                    {...styles.itemContainer}
                >
                    <XngZone contentId={id} zoneName={`Content_${name || index}`} hint={`Zone for ${itemName}`} />
                </StyledWrapper>,
            );
        });

        return (
            <StyledWrapperX
                gap={gap}
                margin={margin}
                css={css`
                    ${styles.container?.css}
                    ${customCSS}
                `}
            >
                {rows}
            </StyledWrapperX>
        );
    }
    // </XNG-Changes>

    for (let row = 0; row < rowCount; row = row + 1) {
        rows.push(
            <GridItem
                key={`GridItem-${id}-${row}`}
                width={[12, 12, 12, 12, 12]}
                // XNG-Changes
                className="row"
                {...styles.gridItem}
            >
                <Zone contentId={id} zoneName={`Content${row}`} />
            </GridItem>,
        );
    }

    const gridCss = css`
        // <XNG-Changes>
        ${({ margin }: any) => {
            if (margin) {
                return css`
                    margin: ${margin};
                `;
            }
        }}
        // </XNG-Changes>
        ${customCSS}
    `;

    return (
        <GridContainer
            css={gridCss}
            gap={gap}
            isFullWidth={isFullWidth}
            // XNG-Changes
            {...{ margin }}
        >
            {rows}
        </GridContainer>
    );
};

const defaultCustomCss = `& {

}
.row > div {

}
`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Rows,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.rowCount,
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
                sortOrder: 3,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.margin,
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.overrideStyle,
                displayName: "Override Style",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.itemsList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Zone";
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.overrideStyle,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Row Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Zone Name",
                        isRequired: true,
                    },
                    {
                        name: "fullWidth",
                        displayName: "Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "addSeparate",
                        displayName: "Add Border Top",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "removeFooterOffset",
                        displayName: "Remove Footer Offset",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tooltip: "Use it to remove footer offset, It'll apply for the last item only",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "CustomColorPickerField" as any,
                        systemListName: "BackGroundColors",
                        defaultValue: "",
                        tooltip: "Background colors are loaded from the system list (BackGroundColors)",
                        sortOrder: 1,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
